/**
 * CustomerMessagingConsent — the per-customer messaging-consent section of the
 * 360 drawer (CD-Docket 2026-08-04 · voyage#426 item 6 · PR #524 Lane 6).
 *
 * Semantics are the docket's, verbatim:
 *  1. WhatsApp is OPT-IN — a missing/false value is NO consent, and the toggles
 *     are never rendered pre-selected unless the server sent an explicit `true`.
 *  2. Email/SMS are OPT-OUT — a missing value means consent (existing semantics).
 *  3. Server-owned suppressions OUTRANK consent: a suppressed channel renders as
 *     locked with its reason instead of a toggle that would do nothing.
 *  4. Saving sends EXPLICIT booleans for every topic — the server persists false.
 *
 * Ops: getCustomerMessagingPreferences / updateCustomerMessagingPreferences.
 * No local data: the record, including suppressions, comes from the API only.
 */
import { useEffect, useState } from "react";
import { Btn, Icon, Pill, T, Toggle } from "@v0y4ge/jrni-ui";
import { updateCustomerMessagingPreferences } from "@v0y4ge/api-client";
import {
  explicitOptIn, explicitOptOut, optInOn, optOutOn, readConsent,
  type ChannelConsent, type ConsentPrefs, type ConsentRecord, type ConsentSuppression, type ConsentTopic,
} from "../journeys/widget-consent";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";

const TOPICS: { id: ConsentTopic; k: string; en: string; subK: string; subEn: string }[] = [
  { id: "bookings", k: "customers.consent.topicBookings", en: "Appointments & bookings", subK: "customers.consent.topicBookingsSub", subEn: "Confirmations, reminders, changes and cancellations." },
  { id: "events", k: "customers.consent.topicEvents", en: "Events", subK: "customers.consent.topicEventsSub", subEn: "Registrations, joining details and follow-ups." },
  { id: "general", k: "customers.consent.topicGeneral", en: "General updates", subK: "customers.consent.topicGeneralSub", subEn: "News, offers and everything not tied to a booking." },
];

type ChannelId = "email" | "sms" | "whatsapp";
const CHANNELS: { id: ChannelId; k: string; en: string }[] = [
  { id: "email", k: "customers.consent.channelEmail", en: "Email" },
  { id: "sms", k: "customers.consent.channelSms", en: "SMS" },
  { id: "whatsapp", k: "customers.consent.channelWhatsapp", en: "WhatsApp" },
];

const suppressionFor = (rec: ConsentRecord | null, ch: ChannelId): ConsentSuppression | null =>
  (rec?.suppressions ?? []).find((s) => String(s.channel) === ch) ?? null;

export default function CustomerMessagingConsent({ customerId }: { customerId: string }) {
  const t = useT();
  const [rec, setRec] = useState<ConsentRecord | null>(null);
  const [draft, setDraft] = useState<ConsentPrefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setUnavailable(false); setRec(null); setDraft(null);
    void readConsent(customerId).then((r) => {
      if (!alive) return;
      if (!r) { setUnavailable(true); setLoading(false); return; }
      setRec(r);
      setDraft({
        email: explicitOptOut(r.preferences.email),
        sms: explicitOptOut(r.preferences.sms),
        whatsapp: explicitOptIn(r.preferences.whatsapp),
      });
      setLoading(false);
    });
    return () => { alive = false; };
  }, [customerId]);

  const shell = (children: React.ReactNode) => (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, overflow: "hidden", flexShrink: 0 }}>
      {children}
    </div>
  );

  if (loading) {
    return shell(<div style={{ padding: "14px 16px", fontSize: 12, color: T.soft }}>{t("customers.consent.loading", "Loading messaging consent…")}</div>);
  }
  if (unavailable || !draft) {
    return shell(
      <div style={{ padding: "14px 16px", display: "flex", gap: 9, alignItems: "flex-start" }}>
        <span style={{ color: T.orange1, display: "inline-flex", marginTop: 1 }}><Icon.warn size={13} /></span>
        <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>{t("customers.consent.unavailable", "Messaging consent couldn't be read for this customer.")}</span>
      </div>,
    );
  }

  const dirty = !!rec && CHANNELS.some((c) => TOPICS.some((tp) => {
    const stored = c.id === "whatsapp" ? optInOn(rec.preferences[c.id], tp.id) : optOutOn(rec.preferences[c.id], tp.id);
    return stored !== (draft[c.id] as ChannelConsent)[tp.id];
  }));

  const set = (ch: ChannelId, topic: ConsentTopic, next: boolean) =>
    setDraft((d) => (d ? { ...d, [ch]: { ...(d[ch] as ChannelConsent), [topic]: next } } : d));

  const save = async () => {
    if (saving || !draft) return;
    setSaving(true);
    try {
      // Explicit booleans for every channel/topic (docket rule 4).
      const body: ConsentPrefs = {
        email: explicitOptOut(draft.email),
        sms: explicitOptOut(draft.sms),
        whatsapp: explicitOptIn(draft.whatsapp),
      };
      const r = await updateCustomerMessagingPreferences(body as never, { customer_id: customerId });
      const payload = r && typeof r === "object" && "data" in (r as Record<string, unknown>) ? (r as { data: ConsentRecord }).data : null;
      // No fake flip: the new state comes from the awaited response, never the draft.
      const fresh = payload ?? await readConsent(customerId);
      if (fresh) {
        setRec(fresh);
        setDraft({
          email: explicitOptOut(fresh.preferences.email),
          sms: explicitOptOut(fresh.preferences.sms),
          whatsapp: explicitOptIn(fresh.preferences.whatsapp),
        });
      }
      toast({ tone: "success", title: t("customers.consent.saved", "Messaging consent saved") });
    } catch (e) {
      toast({ tone: "warn", title: t("customers.consent.saveFailed", "Couldn't save messaging consent"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setSaving(false); }
  };

  return shell(
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${T.n6}` }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("customers.consent.title", "Messaging consent")}</span>
        <span style={{ marginLeft: "auto" }}>
          <Btn size="sm" variant={dirty ? undefined : "secondary"} disabled={!dirty || saving} onClick={() => { void save(); }}>
            {saving ? t("customers.consent.saving", "Saving…") : t("common.actions.save", "Save")}
          </Btn>
        </span>
      </div>
      <p style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5, margin: 0, padding: "10px 16px 0" }}>
        {t("customers.consent.intro", "WhatsApp is only used after the customer has agreed to it. Email and SMS are on unless the customer opts out.")}
      </p>
      <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        {CHANNELS.map((c) => {
          const sup = suppressionFor(rec, c.id);
          const optIn = c.id === "whatsapp";
          return (
            <section key={c.id} style={{ border: `1px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "9px 12px", background: T.n6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t(c.k, c.en)}</span>
                <Pill tone={optIn ? "info" : "neutral"}>{optIn ? t("customers.consent.optIn", "Opt-in") : t("customers.consent.optOut", "Opt-out")}</Pill>
                {sup ? <span style={{ marginLeft: "auto" }}><Pill tone="danger">{t("customers.consent.suppressed", "Suppressed")}</Pill></span> : null}
              </div>
              {sup ? (
                <div style={{ padding: "10px 12px", display: "flex", gap: 9, alignItems: "flex-start" }}>
                  <span style={{ color: T.soft, display: "inline-flex", marginTop: 1 }}><Icon.lock size={12} /></span>
                  <span style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
                    {t("customers.consent.suppressedExplain", "Blocked by the provider, so consent can't re-enable it here.")}
                    {sup.reason ? <span style={{ display: "block", color: T.n1, fontWeight: 600, marginTop: 2 }}>{sup.reason}</span> : null}
                    <span style={{ display: "block", color: T.soft, marginTop: 2 }}>
                      {[sup.code, sup.address, sup.created_at].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </div>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {TOPICS.map((tp, i) => {
                    const on = (draft[c.id] as ChannelConsent)[tp.id] === true;
                    return (
                      <li key={tp.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "9px 12px", borderTop: i ? `1px solid ${T.n6}` : "none" }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t(tp.k, tp.en)}</span>
                          <span style={{ display: "block", fontSize: 11, color: T.soft, lineHeight: 1.5 }}>{t(tp.subK, tp.subEn)}</span>
                        </span>
                        <span style={{ flexShrink: 0, marginTop: 2, opacity: saving ? 0.5 : 1, display: "inline-flex" }}>
                          <Toggle on={on} onChange={saving ? undefined : () => set(c.id, tp.id, !on)} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </>,
  );
}
